import type { TFunction } from 'i18next';

import { MAAS_INFERENCE_SOURCE_FIELD, type MaasInferenceSource } from '@/delivery-type-plugin/capabilities/maas-init';
import { parseFormJsonField } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import { formatMaasAppImageSource } from '@/delivery-type-plugin/capabilities/maas-node-groups/labels';
import {
  buildImageItemFromInferenceSystem,
  parseInferenceFrameworkImageItem,
} from '@/delivery-type-plugin/capabilities/maas-system-config/presetInferenceImage';
import type { ImageSummarySource } from '@/delivery-type-plugin/components';
import { buildImageResourceSummaryItems, resolveImageSummaryFromItem } from '@/delivery-type-plugin/components';

/** 应用镜像预览行（Descriptions / DisplayDescriptor 共用） */
export interface AppInstanceImagePreviewRow {
  id: string;
  label: string;
  value: string;
  /** 商品卡片把架构挂在镜像名称行；配置预览走独立 meta 行 */
  tag?: string;
}

/**
 * 从应用实例表单值解析镜像卡片摘要（名称 / 厂商 / 架构等）。
 * 优先完整 catalog（inferenceFrameworkImage），再回退 `_inferenceSystem` 扁平字段。
 */
function resolveAppInstanceImageSummary(values: Record<string, unknown>): ImageSummarySource | null {
  const catalog = parseInferenceFrameworkImageItem(values.inferenceFrameworkImage);
  if (catalog) return resolveImageSummaryFromItem(catalog);

  const inferenceSystem = parseFormJsonField<Record<string, unknown>>(values._inferenceSystem);
  if (!inferenceSystem) return null;

  const fromSystem = buildImageItemFromInferenceSystem(inferenceSystem);
  return fromSystem ? resolveImageSummaryFromItem(fromSystem) : null;
}

function resolveArchitectureTag(summary: ImageSummarySource | null): string {
  if (!summary) return '';
  if (Array.isArray(summary.cpuArchitectures)) {
    return summary.cpuArchitectures
      .map((item) => String(item).trim())
      .filter(Boolean)
      .join(',');
  }
  return String(summary.cpuArchitectures ?? summary.architecture ?? '').trim();
}

/**
 * 应用镜像模块预览行：镜像来源为首，其后为名称 / 厂商 / 架构等卡片字段。
 */
export function buildAppInstanceImagePreviewRows(values: Record<string, unknown>, t: TFunction): AppInstanceImagePreviewRow[] {
  const rows: AppInstanceImagePreviewRow[] = [];
  const inferenceSystem = parseFormJsonField<Record<string, unknown>>(values._inferenceSystem);
  const imageSourceLabel = formatMaasAppImageSource(
    values[MAAS_INFERENCE_SOURCE_FIELD] as MaasInferenceSource | undefined,
    t,
    inferenceSystem,
  );
  if (imageSourceLabel !== '-') {
    rows.push({
      id: 'app_image_source',
      label: t('data-center:image.source'),
      value: imageSourceLabel,
    });
  }

  const summary = resolveAppInstanceImageSummary(values);
  const items = summary ? buildImageResourceSummaryItems(summary).filter((item) => String(item.value ?? '').trim() !== '') : [];
  const architecture = resolveArchitectureTag(summary);

  items.forEach((item, index) => {
    const value = String(item.value ?? '').trim();
    if (!value || value === '-') return;
    if (index === 0) {
      rows.push({
        id: 'app_image',
        label: t('common:image.name'),
        value,
        ...(architecture ? { tag: architecture } : {}),
      });
      return;
    }
    rows.push({
      id: `app_image_meta_${index}`,
      label: item.label || '-',
      value,
    });
  });

  return rows;
}
